import { stringToBinary, stringToBinaryUnicode, binaryToString } from '@caresend/utils';
import { b64DecodeUnicode, blob2ab, ab2blob, b64EncodeUnicode } from './helpers.js';

/**
 * Decode JWK input to CryptoKey
 * @param key JWK to decode
 * @param type Whether the key is for public or private access
 * @return {Promise<CryptoKey>} Imported CryptoKey ready to be used
 */
const decodeKeyJwk = async (key, type, algorithm = 'RSA') => {
    let keyOps;
    if (key.key_ops)
        keyOps = key.key_ops;
    else
        keyOps = type === 'public' ? ['encrypt', 'wrapKey'] : ['decrypt', 'unwrapKey'];
    const algo = algorithm === 'RSA' ? { name: 'RSA-OAEP', hash: 'SHA-256' } : { name: 'AES-GCM' };
    return crypto.subtle.importKey('jwk', key, algo, true, keyOps);
};
/**
 * Encode CryptoKey to JWK
 * @param key CryptoKey to export
 * @return {Promise<JsonWebKey>} Encoded JWK
 */
const encodeKeyJwk = async (key) => crypto.subtle.exportKey('jwk', key);
/**
 * Create a AES symmetric key that can be decrypted only by the
 * RSA private key associated with the given public asymmetric key
 * @param asymmetric Public crypto key with access to the encrypted key
 * @return {Promise<SymmetricKey>} Object containing new AES key and wrapped key (if public key was specified)
 */
const deriveSymmetricKey = async (asymmetricKey) => {
    const aesKey = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt', 'wrapKey', 'unwrapKey']);
    if (!asymmetricKey)
        return { aesKey };
    const wrappedKey = await crypto.subtle.wrapKey('jwk', aesKey, asymmetricKey, { name: 'RSA-OAEP' });
    const encryptedAesKey = b64EncodeUnicode(binaryToString(wrappedKey));
    return { aesKey, encryptedAesKey };
};
/**
 * Encrypt given RawData object to be decrypted with a private key
 * @param rawData Object to encrypt
 * @param publicKey Public CryptoKey of recipient
 * @return {Promise<string>} Encrypted string
 */
const encrypt = async (rawData, publicKey) => {
    const encodedData = stringToBinaryUnicode(JSON.stringify(rawData));
    // Generate a symmetric key that is only decryptable by the private key matching the public key
    const { aesKey, encryptedAesKey } = await deriveSymmetricKey(publicKey);
    // Encrypt the data with AES-GCM encryption
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encryptedData = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, aesKey, encodedData);
    const aesIv = b64EncodeUnicode(binaryToString(iv));
    const encryptedString = b64EncodeUnicode(binaryToString(encryptedData));
    // Concatenate necessary information
    return `${aesIv} ${encryptedAesKey} ${encryptedString}`;
};
/**
 * Encrypt a given blob
 * @param blob Blob to encrypt
 * @return {Promise<{ blob: Blob, key: AesJsonWebKey }} Encrypted blob and AES key to decrypt
 */
const encryptBlob = async (blob) => {
    // Encode file and derive a key
    const encodedBlob = await blob2ab(blob);
    const { aesKey } = await deriveSymmetricKey();
    // Encrypt the data with AES-GCM encryption
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encryptedData = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, aesKey, encodedBlob);
    const aesIv = b64EncodeUnicode(binaryToString(iv));
    const encryptedBlob = ab2blob(encryptedData);
    const key = await encodeKeyJwk(aesKey);
    key.iv = aesIv;
    // Return encrypted file and respective AES key
    return { blob: encryptedBlob, key };
};
/**
 * Decrypt a given blob with its key
 * @param blob Blob to decrypt
 * @param key Key to use to decrypt
 * @return {Promise<Blob>} Decrypted blob
 */
const decryptBlob = async (blob, key) => {
    // Decode iv value and key from AES key
    const { iv } = key;
    const aesIv = stringToBinary(b64DecodeUnicode(iv));
    const aesKey = await decodeKeyJwk(key, undefined, 'AES');
    // Decrypt file
    const encryptedData = await blob2ab(blob);
    const decryptedData = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: aesIv }, aesKey, encryptedData);
    // Decode response to a new blob
    const decodedBlob = await ab2blob(decryptedData);
    return decodedBlob;
};

export { decodeKeyJwk, decryptBlob, encrypt, encryptBlob };
